import {Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {ComponentType} from '../../../_models/component-type';
import {EventType} from '../../../_models/event-type';
import {ConstantProperty} from '../../../_models/constant-property';
import {ApiService} from '../../../services/api/api.service';
import {ConstantPropertyDataService} from "../../../data/constant-property-data.service";
import {COMMON_FORM_IMPORTS} from "../../../shared/import-groups/Common";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";


@Component({
    selector: 'formula-builder-relationship-rules',
    templateUrl: './formula-builder-relationship-rules.component.html',
    styleUrls: ['./formula-builder-relationship-rules.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
    COMMON_FORM_IMPORTS,
    MatIconButton,
    MatIcon
]
})
export class FormulaBuilderRelationshipRulesComponent implements OnInit {
    @Input() relationship_type: string;
    @Input() second_object: ComponentType | EventType;
    @Output() formulaChange = new EventEmitter<string>();

    private _parentComponent: ComponentType;

    @Input()
    set parentComponent(value: ComponentType) {
        console.log('parentComponent set:', value);
        this._parentComponent = value;
        if (value) {
            this.loadProperties();
        }
    }

    get parentComponent(): ComponentType {
        return this._parentComponent;
    }

    @Input() formula: string = '';
    builder_ready: boolean = false;

    // Properties lists
    parent_properties: ConstantProperty[] = [];
    second_object_properties: ConstantProperty[] = [];

    // Selected properties
    selected_parent_property: ConstantProperty;
    selected_second_property: ConstantProperty;

    // Component name fields
    componentNameProperties = [
        { id: 'name', label: 'Name' },
        { id: 'description', label: 'Description' },
        { id: 'end_time', label: 'End time' },
        { id: 'start_time', label: 'Start time' },
        { id: 'created_on', label: 'Created on' },
        { id: 'changed_on', label: 'Changed on' },
        { id: 'created_by_fk', label: 'Created by' },
        { id: 'changed_by_fk', label: 'Changed by' },
        { id: 'base_type', label: 'Base type' },
        { id: 'json', label: 'Json' },
    ];

    // Event name fields
    eventNameProperties = [
        { id: 'description', label: 'Description' },
        { id: 'end', label: 'End' },
        { id: 'start', label: 'Start' },
        { id: 'created_on', label: 'Created on' },
        { id: 'changed_on', label: 'Changed on' },
        { id: 'created_by_fk', label: 'Created by' },
        { id: 'changed_by_fk', label: 'Changed by' },
        { id: 'base_type', label: 'Base type' },
    ];

    selected_parent_name_prop: string;
    selected_object_name_prop: string;

    constructor(private api: ApiService, private propData: ConstantPropertyDataService) {}

    ngOnInit() {
        console.log('ngOnInit - parentComponent:', this.parentComponent);
        console.log('ngOnInit - second_object:', this.second_object);
        if (this.parentComponent && this.second_object) {
            this.loadProperties();
        }
    }

    private loadProperties() {
        console.log('loadProperties called');
        this.builder_ready = false;

        if (this.parentComponent && this.second_object) {
            console.log('Loading properties for:', this.parentComponent.id, this.second_object.id);
            // Load parent component properties
            this.propData.getPropertiesByRelationshipId(
                this.parentComponent.id, 'component_types'
            ).subscribe({
                next: (response: any) => {
                    this.parent_properties = response.data;
                    this.checkBuilderReady();
                },
                error: (error) => {
                    console.error('Error loading parent properties:', error);
                    this.builder_ready = true;
                }
            });

            const relationshipType = this.relationship_type === 'event' ? 'event_types' : 'component_types';

            // Load second object properties based on relationship type
            this.propData.getPropertiesByRelationshipId(
                this.second_object.id, relationshipType
            ).subscribe({
                next: (response: any) => {
                    this.second_object_properties = response.data;
                    this.checkBuilderReady();
                },
                error: (error) => {
                    console.error('Error loading second object properties:', error);
                    this.builder_ready = true;
                }
            });
        } else {
            console.log('Missing required components:', {
                parent: this.parentComponent,
                second: this.second_object
            });
            this.builder_ready = true;
        }
    }

    private checkBuilderReady() {
        if (Array.isArray(this.parent_properties) && Array.isArray(this.second_object_properties)) {
            this.builder_ready = true;
        }
    }

    addParentProperty(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.selected_parent_property) {
            this.addToFormula(this.selected_parent_property, 'parent');
        }
    }

    addSecondProperty(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.selected_second_property) {
            this.addToFormula(this.selected_second_property, 'second');
        }
    }

    addParentNameProperty(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.selected_parent_name_prop) {
            this.addToFormula({ attributes: { name: this.selected_parent_name_prop } }, 'parent_name');
        }
    }

    addObjectNameProperty(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.selected_object_name_prop) {
            this.addToFormula({ attributes: { name: this.selected_object_name_prop } }, 'object_name');
        }
    }

    private addToFormula(property: any, prefix: string) {
        let new_entry = '';

        switch(prefix) {
            case 'parent':
                new_entry = `[component@${this.parentComponent.attributes.name}@${property.attributes.name}]`;
                break;
            case 'second':
                if (this.relationship_type === 'event') {
                    new_entry = `[event@${this.second_object.attributes.name}@${property.attributes.name}]`;
                } else {
                    new_entry = `[component@${this.second_object.attributes.name}@${property.attributes.name}]`;
                }

                break;
            case 'parent_name':
                new_entry = `[component@${this.parentComponent.attributes.name}@${property.attributes.name}]`;
                break;
            case 'object_name':
                if (this.relationship_type === 'event') {
                    new_entry = `[event@${this.second_object.attributes.name}@${property.attributes.name}]`;
                } else {
                    new_entry = `[component@${this.second_object.attributes.name}@${property.attributes.name}]`;
                }
                break;
        }

        if (!this.formula) {
            this.formula = new_entry;
        } else {
            this.formula += (this.formula.endsWith(' ') ? '' : ' ') + new_entry;
        }

        this.emitFormulaChange();
    }

    onFormulaInput(event: any) {
        this.formula = event.target.value;
        this.emitFormulaChange();
    }

    emitFormulaChange() {
        this.formulaChange.emit(this.formula);
    }
}
